// ClubEditWizard Component
// Multi-step wizard for editing existing clubs

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { auth } from '../../firebaseConfig';
import { notificationSettingsApi } from '../../api/notificationSettings';
import { useSports } from '../../hooks/useSports';
import styles from './ClubEditWizard.module.css';

// Import step components from ClubCreation
import ClubCreationProgress from '../../components/ClubCreation/ClubCreationProgress';
import ClubDetailsStep from '../../components/ClubCreation/steps/ClubDetailsStep';
import ContactInfoStep from '../../components/ClubCreation/steps/ContactInfoStep';
import CategoriesStep from '../../components/ClubCreation/steps/CategoriesStep';
import NotificationsStep from '../../components/ClubCreation/steps/NotificationsStep';

const ClubEditWizard = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { userProfile, refreshUserClubs, userClubs } = useUser();
  const { showSuccess, showError } = useNotification();
  const { sports, sportNames, loading: sportsLoading } = useSports();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const totalSteps = 4;

  // Form data for all steps
  const [formData, setFormData] = useState({
    // Step 1: Club Details
    name: '',
    sport: '',
    type: 'Club',
    businessType: 'Public club',
    description: '',
    membershipType: 'free_open',
    membershipFee: 0,
    clubLogo: null,
    
    // Branding fields
    bannerImage: null,
    primaryColor: null,
    tagline: null,
    socialLinks: {
      website: null,
      facebook: null,
      instagram: null,
      twitter: null,
      youtube: null
    },

    // Step 2: Contact Info
    clubType: 'Physical Location',
    contactNumber: '',
    email: '',
    street: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    location: null,

    // Step 3: Categories
    categories: [],

    // Step 4: Notifications
    notifications: {
      channels: { inApp: true, email: true },
      categories: {
        program: true,
        event: true,
        rsvp: true,
        equipment: true,
        team: true,
        assignment: true
      }
    }
  });

  // Check if user can edit this club
  useEffect(() => {
    if (userClubs && userClubs.length > 0 && clubId) {
      const membership = userClubs.find(m => m.club?._id === clubId);
      if (membership && ['creator', 'owner', 'manager'].includes(membership.role?.toLowerCase())) {
        setCanEdit(true);
      } else {
        setCanEdit(false);
      }
    }
  }, [userClubs, clubId]);

  // Fetch existing club data
  useEffect(() => {
    const fetchClubData = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        // Fetch club details
        const clubRes = await fetch(`${backendUrl}/api/clubs/${clubId}`);
        if (!clubRes.ok) throw new Error('Club not found');
        const club = await clubRes.json();

        // Fetch categories
        const categoriesRes = await fetch(`${backendUrl}/api/categories/club/${clubId}`);
        let existingCategories = [];
        if (categoriesRes.ok) {
          existingCategories = await categoriesRes.json();
        }

        // Fetch notification settings
        let notificationSettings = {
          channels: { inApp: true, email: true },
          categories: {
            program: true,
            event: true,
            rsvp: true,
            equipment: true,
            team: true,
            assignment: true
          }
        };

        try {
          const notifRes = await notificationSettingsApi.getSettings(clubId);
          
          // API returns { success: true, settings: { globalSettings, categories, ... } }
          const settings = notifRes?.settings;
          
          if (notifRes?.success && settings?.globalSettings) {
            // Transform backend settings to form structure
            notificationSettings = {
              channels: {
                inApp: settings.globalSettings.defaultChannels?.inApp ?? true,
                email: settings.globalSettings.defaultChannels?.email ?? true
              },
              categories: {
                program: settings.categories?.program?.categoryEnabled ?? true,
                event: settings.categories?.event?.categoryEnabled ?? true,
                rsvp: settings.categories?.rsvp?.categoryEnabled ?? true,
                equipment: settings.categories?.equipment?.categoryEnabled ?? true,
                team: settings.categories?.team?.categoryEnabled ?? true,
                assignment: settings.categories?.assignment?.categoryEnabled ?? true
              }
            };
          }
        } catch (notifErr) {
          console.warn('Could not fetch notification settings, using defaults:', notifErr);
        }

        // Store original categories for comparison later
        setOriginalCategories(existingCategories.map(c => ({ ...c })));

        // Transform categories to match form structure - only existing categories are shown
        const categoriesForForm = existingCategories.map(cat => ({
          id: cat._id,
          name: cat.name,
          description: cat.description || '',
          enabled: true, // Existing categories are enabled by definition
          isDefault: false,
          isExisting: true // Mark as existing for update logic
        }));

        // Helper to extract banner URL
        const getBannerValue = (banner) => {
          if (!banner) return null;
          if (typeof banner === 'string') return { url: banner };
          return banner;
        };

        // Populate form data
        setFormData({
          name: club.name || '',
          sport: club.sport || '',
          type: club.type || 'Club',
          businessType: club.businessType || 'Public club',
          description: club.description || '',
          membershipType: club.membershipType || 'free_open',
          membershipFee: club.membershipFee || 0,
          clubLogo: club.clubLogo ? { url: typeof club.clubLogo === 'string' ? club.clubLogo : club.clubLogo.url } : null,
          // Branding fields
          bannerImage: getBannerValue(club.bannerImage),
          primaryColor: club.primaryColor || null,
          tagline: club.tagline || null,
          socialLinks: {
            website: club.socialLinks?.website || null,
            facebook: club.socialLinks?.facebook || null,
            instagram: club.socialLinks?.instagram || null,
            twitter: club.socialLinks?.twitter || null,
            youtube: club.socialLinks?.youtube || null
          },
          // Contact info
          clubType: club.clubType || 'Physical Location',
          contactNumber: club.contactNumber || '',
          email: club.email || '',
          street: club.street || '',
          city: club.city || '',
          state: club.state || '',
          country: club.country || '',
          zipCode: club.zipCode || '',
          location: club.location || null,
          categories: categoriesForForm,
          notifications: notificationSettings
        });

      } catch (err) {
        console.error('Error fetching club data:', err);
        setLoadError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (clubId) {
      fetchClubData();
    }
  }, [clubId, backendUrl]);

  // Update form data
  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Validate current step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.name.trim().length >= 3 &&
          formData.sport !== ''
        );
      
      case 2:
        if (formData.clubType === 'Physical Location') {
          return (
            formData.contactNumber.trim() !== '' &&
            formData.street.trim() !== '' &&
            formData.city.trim() !== '' &&
            formData.state.trim() !== '' &&
            formData.zipCode.trim() !== ''
          );
        } else {
          return (
            formData.contactNumber.trim() !== '' &&
            formData.city.trim() !== ''
          );
        }
      
      case 3:
        return formData.categories.some(cat => cat.enabled);
      
      case 4:
        return true;
      
      default:
        return false;
    }
  };

  // Handle Next button
  const handleNext = () => {
    if (!validateStep(currentStep)) {
      if (currentStep === 1) {
        showError('Please fill in club name (min 3 characters) and select a sport.');
      } else if (currentStep === 2) {
        showError('Please fill in all required contact information.');
      } else if (currentStep === 3) {
        showError('Please enable at least one category.');
      }
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle Back button
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle cancel/close
  const handleClose = () => {
    navigate(`/club/${clubId}`);
  };

  // Handle final submission
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await auth.currentUser?.getIdToken();

      // 1. Update Club
      const clubPayload = {
        name: formData.name,
        sport: formData.sport,
        type: formData.type,
        businessType: formData.businessType,
        description: formData.description || '',
        membershipType: formData.membershipType,
        membershipFee: formData.membershipType === 'paid' ? formData.membershipFee : 0,
        clubLogo: formData.clubLogo?.url || formData.clubLogo || '',
        // Branding fields
        bannerImage: formData.bannerImage || null,
        primaryColor: formData.primaryColor || null,
        tagline: formData.tagline || null,
        socialLinks: formData.socialLinks || {},
        // Contact info
        clubType: formData.clubType,
        contactNumber: formData.contactNumber,
        email: formData.email || '',
        street: formData.street || '',
        city: formData.city,
        state: formData.state || '',
        country: formData.country || '',
        zipCode: formData.zipCode || '',
        location: formData.location
      };

      const clubResponse = await fetch(`${backendUrl}/api/clubs/${clubId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clubPayload)
      });

      if (!clubResponse.ok) {
        const errorData = await clubResponse.json().catch(() => ({}));
        console.error('Club update error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to update club');
      }

      // 2. Handle Categories (create new, delete removed)
      const currentEnabledCategories = formData.categories.filter(cat => cat.enabled);
      const originalCategoryIds = originalCategories.map(c => c._id);
      
      // Find new categories to create (no isExisting flag)
      const newCategories = currentEnabledCategories.filter(cat => !cat.isExisting);
      
      // Find categories to delete (in original but not in current enabled)
      const currentCategoryIds = currentEnabledCategories
        .filter(cat => cat.isExisting)
        .map(cat => cat.id);
      const categoriesToDelete = originalCategoryIds.filter(id => !currentCategoryIds.includes(id));

      // Create new categories
      if (newCategories.length > 0) {
        await Promise.all(
          newCategories.map(cat =>
            fetch(`${backendUrl}/api/categories`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                name: cat.name,
                description: cat.description,
                clubId: clubId,
                createdBy: userProfile?._id
              })
            })
          )
        );
      }

      // Delete removed categories
      if (categoriesToDelete.length > 0) {
        await Promise.all(
          categoriesToDelete.map(catId =>
            fetch(`${backendUrl}/api/categories/${catId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
          )
        );
      }

      // 3. Update notification category settings
      try {
        const notifSettings = formData.notifications;
        
        // Update each category's enabled status
        // Backend expects: { "enabled": boolean }
        const categoryNames = ['program', 'event', 'rsvp', 'equipment', 'team', 'assignment'];
        
        for (const category of categoryNames) {
          const isEnabled = notifSettings.categories?.[category] ?? true;
          await fetch(`${backendUrl}/api/clubs/${clubId}/notification-settings/category/${category}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              enabled: isEnabled
            })
          });
        }
        
        console.log('Notification settings updated successfully');
      } catch (notifError) {
        console.warn('Could not update notification settings:', notifError);
        // Don't fail club update if notification update fails
      }

      // 4. Refresh user clubs
      refreshUserClubs();

      showSuccess('Club updated successfully!');
      navigate(`/club/${clubId}`);

    } catch (error) {
      console.error('Error updating club:', error);
      showError(error.message || 'Failed to update club. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ClubDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            sportsOptions={sportNames}
            sportsData={sports}
            sportsLoading={sportsLoading}
            onNext={handleNext}
            onClose={handleClose}
            isEditMode={true}
          />
        );
      
      case 2:
        return (
          <ContactInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      
      case 3:
        return (
          <CategoriesStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            isEditMode={true}
          />
        );
      
      case 4:
        return (
          <NotificationsStep
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            submitButtonText="Save Changes"
          />
        );
      
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <ClipLoader color="var(--primary)" size={50} />
          <p>Loading club details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Error</h2>
          <p>{loadError}</p>
          <button className={styles.backBtn} onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Permission check
  if (!canEdit && !isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Access Denied</h2>
          <p>You don't have permission to edit this club.</p>
          <button className={styles.backBtn} onClick={() => navigate(`/club/${clubId}`)}>
            Back to Club
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wizard}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Edit Club</h1>
            <p className={styles.subtitle}>Update your club information</p>
          </div>
          <button 
            className={styles.closeBtn}
            onClick={handleClose}
            title="Cancel and go back"
          >
            <FaTimes />
          </button>
        </div>

        {/* Progress Indicator */}
        <ClubCreationProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
        />

        {/* Step Content */}
        <div className={styles.stepContent}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default ClubEditWizard;

